import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft, ScrollText, User, FileText, Shield, LogIn, Pencil, Ban, KeyRound,
  UserPlus, Trash2, ShieldAlert, Download, FileSpreadsheet, FileText as FileTextIcon,
  CalendarClock, Globe, ChevronLeft, ChevronRight, ShieldCheck, Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ACTION_CONFIG = {
  login: { icon: LogIn, color: "bg-blue-100 text-blue-700", label: "Login" },
  login_failed: { icon: Lock, color: "bg-red-100 text-red-700", label: "Login Failed" },
  account_locked: { icon: Lock, color: "bg-red-200 text-red-900", label: "Account Locked" },
  twofa_challenge: { icon: ShieldCheck, color: "bg-indigo-100 text-indigo-700", label: "2FA Challenge" },
  twofa_enabled: { icon: ShieldCheck, color: "bg-emerald-100 text-emerald-700", label: "2FA Enabled" },
  twofa_disabled: { icon: ShieldCheck, color: "bg-amber-100 text-amber-800", label: "2FA Disabled" },
  twofa_failed: { icon: ShieldAlert, color: "bg-red-100 text-red-700", label: "2FA Failed" },
  twofa_setup_started: { icon: ShieldCheck, color: "bg-indigo-100 text-indigo-700", label: "2FA Setup" },
  security_settings_updated: { icon: Shield, color: "bg-purple-100 text-purple-700", label: "Security Settings" },
  create_request: { icon: FileText, color: "bg-emerald-100 text-emerald-700", label: "Create Request" },
  edit_request: { icon: Pencil, color: "bg-amber-100 text-amber-700", label: "Edit Request" },
  cancel_request: { icon: Ban, color: "bg-red-100 text-red-700", label: "Cancel Request" },
  dept_approval: { icon: Shield, color: "bg-purple-100 text-purple-700", label: "Dept Approval" },
  cmo_approval: { icon: Shield, color: "bg-indigo-100 text-indigo-700", label: "CMO Approval" },
  cmo_on_behalf_approval: { icon: ShieldAlert, color: "bg-amber-100 text-amber-800", label: "CMO On Behalf" },
  housing_approval: { icon: Shield, color: "bg-teal-100 text-teal-700", label: "Housing Approval" },
  housing_date_adjusted: { icon: CalendarClock, color: "bg-amber-100 text-amber-800", label: "Dates Adjusted" },
  create_user: { icon: UserPlus, color: "bg-green-100 text-green-700", label: "Create User" },
  update_user: { icon: Pencil, color: "bg-amber-100 text-amber-700", label: "Update User" },
  delete_user: { icon: Trash2, color: "bg-red-100 text-red-700", label: "Delete User" },
  reset_password: { icon: KeyRound, color: "bg-orange-100 text-orange-700", label: "Reset Password" },
  change_password: { icon: KeyRound, color: "bg-orange-100 text-orange-700", label: "Change Password" },
};

const PAGE_SIZE = 100;

export default function AuditLog() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("activity");
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hits, setHits] = useState([]);
  const [hitsTotal, setHitsTotal] = useState(0);
  const [hitsPage, setHitsPage] = useState(0);
  const [ipFilter, setIpFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [onBehalfOnly, setOnBehalfOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit-logs?limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}`);
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page]);

  const fetchHits = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(hitsPage * PAGE_SIZE) });
      if (ipFilter.trim()) params.set("ip", ipFilter.trim());
      const { data } = await api.get(`/security/access-logs?${params.toString()}`);
      setHits(data.items || []);
      setHitsTotal(data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hitsPage, ipFilter]);

  useEffect(() => { if (tab === "activity") fetchLogs(); }, [tab, fetchLogs]);
  useEffect(() => {
    if (tab !== "hits") return;
    const t = setTimeout(fetchHits, 300);
    return () => clearTimeout(t);
  }, [tab, fetchHits]);

  const onBehalfCount = useMemo(
    () => logs.filter((l) => l.action === "cmo_on_behalf_approval").length,
    [logs]
  );

  const displayed = useMemo(
    () => (onBehalfOnly ? logs.filter((l) => l.action === "cmo_on_behalf_approval") : logs),
    [logs, onBehalfOnly]
  );

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (onBehalfOnly) params.set("action", "cmo_on_behalf_approval");
      const response = await api.get(`/audit-logs/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const suffix = onBehalfOnly ? "cmo_on_behalf" : "all";
      link.download = `audit_log_${suffix}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Audit log exported (${format.toUpperCase()})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const Pager = ({ current, totalCount, onChange }) => {
    const pages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    return (
      <div className="flex items-center justify-between pt-3 text-sm text-[#7A8A84]" data-testid="audit-pager">
        <span>{totalCount.toLocaleString()} entries · page {current + 1} of {pages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={current === 0} onClick={() => onChange(current - 1)} data-testid="audit-prev">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={current + 1 >= pages} onClick={() => onChange(current + 1)} data-testid="audit-next">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 fade-in" data-testid="audit-log-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58] hover:text-[#1C1F1E]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Audit Log
        </h1>
        <p className="text-[#525C58] mt-1">System activity, IP tracking and compliance trail — kept forever, never trimmed</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F4F4F1] border border-[#E8E8E6]">
          <TabsTrigger value="activity" data-testid="audit-tab-activity">
            <ScrollText className="h-4 w-4 mr-2" /> Activity
          </TabsTrigger>
          <TabsTrigger value="hits" data-testid="audit-tab-hits">
            <Globe className="h-4 w-4 mr-2" /> Page hits (IP)
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "activity" && (
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ScrollText className="h-5 w-5 text-[#40916C]" /> Activity ({total.toLocaleString()})
            </CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              <div
                className="flex items-center gap-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50"
                data-testid="on-behalf-filter-wrap"
              >
                <ShieldAlert className="h-4 w-4 text-amber-700" />
                <Label htmlFor="on-behalf-toggle" className="text-sm text-amber-900 cursor-pointer select-none">
                  CMO acted on behalf only
                  {onBehalfCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium bg-amber-200 text-amber-900 rounded-full" data-testid="on-behalf-count">
                      {onBehalfCount}
                    </span>
                  )}
                </Label>
                <Switch
                  id="on-behalf-toggle"
                  checked={onBehalfOnly}
                  onCheckedChange={setOnBehalfOnly}
                  data-testid="on-behalf-toggle"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors"
                    disabled={exporting || displayed.length === 0}
                    data-testid="audit-export-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exporting ? "Exporting…" : "Export"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-[#7A8A84] font-normal">
                    {onBehalfOnly
                      ? `Filtered: ${onBehalfCount} on-behalf entries`
                      : `All ${total.toLocaleString()} entries`}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleExport("xlsx")}
                    className="cursor-pointer"
                    data-testid="audit-export-xlsx"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />
                    Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleExport("csv")}
                    className="cursor-pointer"
                    data-testid="audit-export-csv"
                  >
                    <FileTextIcon className="h-4 w-4 mr-2 text-blue-600" />
                    CSV (.csv)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : displayed.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-12">
              {onBehalfOnly ? "No CMO on-behalf approvals recorded." : "No audit logs yet"}
            </p>
          ) : (
            <>
            <Table data-testid="audit-logs-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[#7A8A84] font-medium w-44">Timestamp</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">User</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">Action</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">IP Address</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map((log) => {
                  const config = ACTION_CONFIG[log.action] || { icon: ScrollText, color: "bg-gray-100 text-gray-700", label: log.action };
                  const IconComp = config.icon;
                  return (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-[#7A8A84] text-sm whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-[#7A8A84]" />
                          <span className="text-[#1C1F1E] text-sm font-medium">{log.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${config.color} border-0 text-xs gap-1`}>
                          <IconComp className="h-3 w-3" /> {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#525C58] text-sm font-mono" data-testid={`audit-ip-${log.id}`}>
                        {log.ip || "—"}
                      </TableCell>
                      <TableCell className="text-[#525C58] text-sm max-w-md truncate" title={log.details}>
                        {log.details}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pager current={page} totalCount={total} onChange={setPage} />
            </>
          )}
        </CardContent>
      </Card>
      )}

      {tab === "hits" && (
      <Card className="border-[#E8E8E6]" data-testid="access-logs-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Globe className="h-5 w-5 text-[#40916C]" /> Page hits ({hitsTotal.toLocaleString()})
            </CardTitle>
            <div className="w-56">
              <Input value={ipFilter} onChange={(e) => { setIpFilter(e.target.value); setHitsPage(0); }}
                     placeholder="Filter by IP address…" className="h-9 font-mono text-sm" data-testid="ip-filter-input" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : hits.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-12">No page hits recorded yet</p>
          ) : (
            <>
            <Table data-testid="access-logs-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[#7A8A84] font-medium w-44">Timestamp</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">IP Address</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">User</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">Method</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">Page / endpoint</TableHead>
                  <TableHead className="text-[#7A8A84] font-medium">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hits.map((h) => (
                  <TableRow key={h.id} data-testid={`hit-row-${h.id}`}>
                    <TableCell className="text-[#7A8A84] text-sm whitespace-nowrap">
                      {new Date(h.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-[#1C1F1E]">{h.ip || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {h.anonymous ? (
                        <Badge className="bg-stone-100 text-stone-600 border-0 text-[10px]">anonymous</Badge>
                      ) : (
                        <span className="text-[#1C1F1E]">{h.user_name || h.user_email}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-[#525C58]">{h.method}</TableCell>
                    <TableCell className="text-xs font-mono text-[#525C58] max-w-sm truncate" title={h.path}>{h.path}</TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-[10px] ${h.status < 400 ? "bg-emerald-100 text-emerald-800" : h.status === 401 || h.status === 403 || h.status === 423 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                        {h.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager current={hitsPage} totalCount={hitsTotal} onChange={setHitsPage} />
            </>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
